import { delegate } from '@atomify/core';
import { Component } from '@atomify/hooks';
import { openModal } from '@source/store/modals';
import { elements } from '@utilities/dom';
import { translateItem } from '@utilities/hooks';

interface LoadableModalOptions {
    name: string;
    loader: () => Promise<any>;
    modalId?: string;
    htmlTag?: string;
}

const LOADING_CLASS = 'is-loading';
const DATA_ATTRIBUTES = 'data-attributes';

const LoadableModal = (options: LoadableModalOptions) => {
    let loaderIsCalled = false;

    const selector = `[js-hook-loadable-modal="${getModalId(options)}"]`;

    // Preload chunk on mouseover
    delegate({
        selector,
        target: document,
        type: 'mouseover',
        callback: () => {
            if (!loaderIsCalled) {
                options.loader();
                loaderIsCalled = true;
            }
        },
    });

    // Load modal on click
    delegate({
        selector,
        target: document,
        type: 'click',
        callback: (event) => {
            const target = (event as any).delegateTarget as HTMLButtonElement;

            if (modalExists(options)) {
                openModal(getModalId(options));
            } else {
                loadModal(target, options);
            }
        },
    });
};

const getModalId = (options: LoadableModalOptions) => options.modalId || options.name;

const getHtmlTag = (options: LoadableModalOptions) => options.htmlTag || options.name;

const modalExists = (options: LoadableModalOptions) =>
    !!document.querySelector(getHtmlTag(options));

const renderModal = (button: HTMLButtonElement, options: LoadableModalOptions) => {
    const attributesJSON = button.getAttribute(DATA_ATTRIBUTES);
    const customElement = document.createElement(options.htmlTag || options.name) as Component;

    if (attributesJSON) {
        try {
            const attributes = JSON.parse(attributesJSON);

            for (const key in attributes) {
                const value = attributes[key]!;

                customElement.setAttribute(key, value);
            }
        } catch (err) {
            console.error(`Error parsing ${DATA_ATTRIBUTES}: `, err);
        }
    }

    elements.body.append(customElement);

    return customElement;
};

const loadModal = async (button: HTMLButtonElement, options: LoadableModalOptions) => {
    button.classList.add(LOADING_CLASS);

    try {
        // Wait for dictionary to be loaded
        await translateItem('');

        // Wait for import to be loaded
        await options.loader();

        // Create and append the custom element
        const customElement = renderModal(button, options);
        if (!customElement.connected) await customElement.componentOnReady();

        // Wait untill nested modal is ready, then open the modal
        const customElementModal = customElement.querySelector('bpd-modal') as
            | Component
            | undefined;

        if (customElementModal) {
            if (!customElementModal.connected) await customElementModal.componentOnReady();

            // setTimout makes sure the modal animation is triggered. Without it's instant
            setTimeout(() => {
                openModal(getModalId(options));
            }, 50);
        }
    } finally {
        button.classList.remove(LOADING_CLASS);
    }
};

export { LoadableModal };
